import { lazyPage, type ModulePlugin } from "@keel/core";

const accounts = () => import("./Accounts");
const ledgers = () => import("./Ledgers");
const Accounts = lazyPage(accounts, "Accounts");
const Journal = lazyPage(accounts, "Journal");
const GeneralLedger = lazyPage(ledgers, "GeneralLedger");
const TrialBalance = lazyPage(ledgers, "TrialBalance");
const PostingGroups = lazyPage(ledgers, "PostingGroups");
const PostingPanel = lazyPage(ledgers, "PostingPanel");
const Reports = lazyPage(() => import("./Reports"), "Reports");
const Periods = lazyPage(() => import("./Periods"), "Periods");
const GroupReport = lazyPage(() => import("./Group"), "GroupReport");
const Dimensions = lazyPage(() => import("./Dimensions"), "Dimensions");
const AccountingSetup = lazyPage(() => import("./Setup"), "AccountingSetup");
const Accruals = lazyPage(() => import("./Accruals"), "Accruals");
const AnnualReport = lazyPage(() => import("./AnnualReport"), "AnnualReport");
const RecurringEntries = lazyPage(() => import("./Recurring"), "RecurringEntries");
const budgetsPage = () => import("./Budgets");
const Budgets = lazyPage(budgetsPage, "Budgets");
const BudgetPage = lazyPage(budgetsPage, "BudgetPage");
const PostingSetupTab = lazyPage(() => import("./PostingSetup"), "PostingSetupTab");

/**
 * Accounting: chart of accounts, journal, general ledger, trial balance, posting groups.
 * Ported from the prototype's `pages/finance/Accounting.tsx` and `Ledgers.tsx`. Adds the
 * "Accounting" panel (posting group per legal entity) to customer and supplier cards.
 */
const plugin: ModulePlugin = {
  key: "accounting",
  help: () => import("./help"),
  nav: [
    {
      group: "Accounting",
      items: [
        { to: "/ledger", label: "General ledger", icon: "ledger", perm: "accounting.journal.read" },
        { to: "/journal", label: "Journal", icon: "book", perm: "accounting.journal.read" },
        { to: "/accruals", label: "Accruals & deferrals", icon: "calendar", perm: "accounting.journal.read" },
        {
          to: "/recurring-entries",
          label: "Recurring entries",
          icon: "refresh",
          perm: "accounting.journal.read",
        },
        { to: "/trial-balance", label: "Trial balance", icon: "sliders", perm: "accounting.reports.read" },
        { to: "/reports", label: "Financial statements", icon: "chart", perm: "accounting.reports.read" },
        { to: "/budgets", label: "Budgets", icon: "gauge", perm: "accounting.reports.read" },
        { to: "/annual-report", label: "Annual report", icon: "file", perm: "accounting.reports.read" },
        { to: "/group-report", label: "Group report", icon: "grid", perm: "accounting.reports.read" },
        { to: "/accounts", label: "Chart of accounts", icon: "building", perm: "accounting.accounts.read" },
        {
          to: "/posting-groups",
          label: "Posting groups",
          icon: "settings",
          perm: "accounting.posting_groups.read",
        },
        { to: "/dimensions", label: "Dimensions", icon: "grid", perm: "accounting.accounts.read" },
        {
          to: "/accounting-setup",
          label: "Statement lines & tax",
          icon: "sliders",
          perm: "accounting.accounts.read",
        },
      ],
    },
  ],
  settingsTabs: [
    {
      key: "posting",
      label: "Posting setup",
      perm: "accounting.posting_groups.read",
      element: <PostingSetupTab />,
    },
  ],
  routes: [
    { path: "/ledger", element: <GeneralLedger /> },
    { path: "/journal", element: <Journal /> },
    { path: "/accruals", element: <Accruals /> },
    { path: "/recurring-entries", element: <RecurringEntries /> },
    { path: "/budgets", element: <Budgets /> },
    { path: "/annual-report", element: <AnnualReport /> },
    { path: "/budgets/:id", element: <BudgetPage /> },
    { path: "/trial-balance", element: <TrialBalance /> },
    { path: "/accounts", element: <Accounts /> },
    { path: "/posting-groups", element: <PostingGroups /> },
    { path: "/reports", element: <Reports /> },
    { path: "/periods", element: <Periods /> },
    { path: "/group-report", element: <GroupReport /> },
    { path: "/dimensions", element: <Dimensions /> },
    { path: "/accounting-setup", element: <AccountingSetup /> },
  ],
  commands: [
    ["New journal entry", "/journal?new=1", "plus", "accounting.journal.write"],
    ["General ledger", "/ledger", "ledger", "accounting.journal.read"],
    ["Journal", "/journal", "book", "accounting.journal.read"],
    ["Accruals & deferrals", "/accruals", "calendar", "accounting.journal.read"],
    [
      "Recurring entries (rent, standing entries)",
      "/recurring-entries",
      "refresh",
      "accounting.journal.read",
    ],
    ["Trial balance", "/trial-balance", "sliders", "accounting.reports.read"],
    ["Chart of accounts", "/accounts", "building", "accounting.accounts.read"],
    ["Posting groups", "/posting-groups", "settings", "accounting.posting_groups.read"],
    ["Profit & loss", "/reports", "chart", "accounting.reports.read"],
    ["Balance sheet", "/reports?tab=balance", "chart", "accounting.reports.read"],
    ["Cash flow", "/reports?tab=cash", "chart", "accounting.reports.read"],
    ["Budgets (budget against actual)", "/budgets", "gauge", "accounting.reports.read"],
    ["Annual report (gada pārskats, EDS)", "/annual-report", "file", "accounting.reports.read"],
    ["Group report (consolidated)", "/group-report", "grid", "accounting.reports.read"],
    ["Year-end close", "/periods", "calendar", "accounting.periods.write"],
    ["Dimensions (cost centres, projects)", "/dimensions", "grid", "accounting.accounts.read"],
    ["Statement lines & tax codes", "/accounting-setup", "sliders", "accounting.accounts.read"],
  ],
  newMenu: [
    { label: "Journal entry", to: "/journal?new=1", perm: "accounting.journal.write", section: "Accounting" },
  ],
  partyPanels: [{ key: "posting", perm: "accounting.posting_groups.read", component: PostingPanel }],
};
export default plugin;
